import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Dialog, TextField, Typography, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../../hooks/useUser';

import {
  setSelectedMenuItem,
  setCart,
  setMenuSearchText,
  addUpdatingCartItems,
  setCartItems,
} from '../../../redux/legacy/reducers/ducks/legacy/OrderDuck';
import { routeNames } from '../../../util/Constants';
import MenuItemQuestions from './MenuItemQuestions';
import IncDecBtnGroup from '../../input/IncDecBtnGroup';
import { getTimeParams, countMenuItemPrice } from '../../../util/Restaurant';
import { formatCartForApi } from '../../../util/Helpers';
import Notification from '../Notification';

function AddToCartPanel() {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [comment, setComment] = useState('');

  const dispatch = useDispatch();
  const location = useLocation();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { userData } = useUser();

  const {
    cartItems,
    menuItem,
    orderType,
    selectedMenuItem,
    menuItems,
    selectedRestaurant,
    time,
    address,
  } = useSelector(
    ({ restaurant, order, address: selectedAddress, time: selectedTime }) => ({
      cartItems: order.cartItems,
      menuItems: restaurant?.selectedRestaurantMenu?.menuItems,
      menuItem:
        restaurant?.selectedRestaurantMenu?.menuItems?.[
          order.selectedMenuItem?.id
        ],
      selectedRestaurant: restaurant?.selectedRestaurantId,
      orderType: order?.type,
      selectedMenuItem: order.selectedMenuItem,
      address: selectedAddress.selectedAddress,
      time: selectedTime.selectedTime,
    })
  );

  useEffect(() => {
    if (Object.values(menuItems || {}).length && cartItems.length) {
      const keys = Object.keys(menuItems || {});
      const missingItems = [];

      const updatedListItems = cartItems.filter((item) => {
        if (!keys.includes(item.menuItemId.toString())) {
          missingItems.push(item.name);
          return false;
        }
        return true;
      });

      if (missingItems.length > 0) {
        // This spammed on Atelier Florian without a reason
        Notification.warning(
          `${missingItems.length <= 1 ? 'Item' : 'Items'} ${missingItems.join(
            ', '
          )} no longer valid`
        );
        // dispatch(setCart({ cart: updatedListItems }));
        dispatch(setCartItems(updatedListItems));
      }
    }
  }, [cartItems, menuItems, dispatch]);

  useEffect(() => {
    if (!selectedMenuItem) return;

    setSelectedOptions(selectedMenuItem.answers || []);
    setQuantity(selectedMenuItem.quantity || 1);
    setComment(selectedMenuItem.comment || '');
  }, [selectedMenuItem]);

  const close = useCallback(() => {
    setSelectedOptions([]);
    dispatch(setSelectedMenuItem(null));
  }, [dispatch]);

  const onQuantityChange = useCallback((newQuantity) => {
    setQuantity(newQuantity);
  }, []);

  const handleQuestionsChange = useCallback(
    (values) => {
      setSelectedOptions(values);
    },
    [menuItems]
  );

  const totalPrice = useMemo(() => {
    const menuItemPrice = countMenuItemPrice({
      menuItem,
      selectedOptions,
      orderType,
      quantity,
    });
    return menuItemPrice.toFixed(2);
  }, [menuItem, quantity, selectedOptions, orderType]);

  const isValid = useMemo(() => {
    if (menuItem?.min) {
      return menuItem.min <= quantity;
    }

    const requiredOptions = menuItem?.questions?.filter((question) => {
      if (!question.sub) {
        return question.required;
      }

      const child = selectedOptions.find(
        (option) => option.child === question.id
      );

      if (child) {
        return question.required;
      }

      return false;
    });

    const valid = requiredOptions?.reduce((accValid, question) => {
      const selectedItemsCount = selectedOptions.reduce((acc, option) => {
        if (question.id === option.parent) {
          return acc + option.quantity * option.weight;
        }

        return acc;
      }, 0);

      if (
        selectedItemsCount === 0 ||
        (question.min && selectedItemsCount < question.min)
      ) {
        return false;
      }

      return accValid;
    }, true);

    return valid;
  }, [selectedOptions, quantity, menuItem]);

  const onSubmit = () => {
    const newCartItem = {
      comment,
      quantity,
      name: menuItem.name,
      answers: selectedOptions,
      sort: selectedMenuItem.sort,
      menuItemId: menuItem.id,
      price: menuItem.prices[orderType.value],
    };

    let newCartItems = [...cartItems];

    if (newCartItem.sort) {
      const index = newCartItems.findIndex(
        (item) => item.sort === newCartItem.sort
      );
      newCartItems[index] = newCartItem;
    } else {
      let newSort = 0;
      newCartItems.forEach((item) => {
        if (item.sort > newSort) {
          newSort = item.sort;
        }
      });
      newSort += 1;
      newCartItem.sort = newSort;
      newCartItems = [...newCartItems, newCartItem].sort((a, b) =>
        a.sort > b.sort ? 1 : -1
      );
    }
    const newCartData = {
      cart: newCartItems,
      sort: newCartItem.sort,
    };
    // dispatch(setCartItems(newCartData.cart));
    const newCart = formatCartForApi(newCartData.cart);

    const payload = {
      payloadCartData: newCartData,
      apiData: {
        client: selectedRestaurant,
        data: {
          mode: orderType.value,
          ...getTimeParams(time),
          cart: newCart,
          address: address?.id,
        },
      },
      userProfile: userData,
      pathname: location.pathname === routeNames.checkout,
    };

    dispatch(setCartItems(newCartData.cart));
    dispatch(addUpdatingCartItems(newCartItem.sort));
    dispatch(setCart(payload));
    dispatch(setMenuSearchText(''));
    close();
  };

  if (!menuItem || !selectedMenuItem) {
    return null;
  }

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={!!menuItem && !!selectedMenuItem}
      fullScreen={fullScreen}
      onClose={close}
      sx={{ maxHeight: '100%' }}
    >
      {menuItem && selectedMenuItem && (
        <Box
          sx={{
            position: 'relative',
            paddingTop: '20px',
            paddingBottom: '70px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'left',
            justifyItems: 'left',
            textAlign: 'left',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              mx: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              flexWrap: 'no-wrap',
              alignItems: 'left',
              justifyItems: 'left',
            }}
          >
            <Box data-test-id="CartPanelHeader" sx={{ height: '40px' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {menuItem.name}
              </Typography>
              <IconButton
                sx={{
                  position: 'absolute',
                  margin: '16px',
                  right: 0,
                  top: 0,
                  padding: 1,
                }}
                onClick={close}
                aria-label="close"
                color="primary"
                data-test-id="cartpanel-close-btn"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box
              data-test-id="CartPanelOptions"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'left',
                justifyItems: 'left',
                maxHeight: '60vh',
                height: '100%',
                overflowY: 'auto',
                paddingBottom: 2,
              }}
            >
              <Box direction="column" spacing={3}>
                <Typography variant="body2" color="text.secondary" mt={2}>
                  {menuItem.description}
                </Typography>
                <Box data-test-id="CartItemOptionsArea">
                  <MenuItemQuestions
                    menuItem={menuItem}
                    orderType={orderType}
                    onChange={handleQuestionsChange}
                    selectedOptions={selectedOptions}
                    data-test-id="menu-item-questions"
                  />
                </Box>
                <Box data-test-id="CartItemCommentArea">
                  <Typography variant="body1" mt={3} mb={1} fontWeight={600}>
                    Any Specific Requirements?
                  </Typography>
                  <TextField
                    placeholder="Do you have any food allergies or specific requirements the restaurant should know for this item?"
                    inputProps={{
                      'data-test-id': 'specialCommentTextArea',
                    }}
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    value={comment}
                    data-test-id="DontUseThisOne"
                    onChange={(e) => setComment(e.target.value)}
                  />
                </Box>

                {menuItem.min && (
                  <Box data-test-id="CartItemMinimumArea">
                    <Typography variant="body2">
                      {`Minimum quantity: ${menuItem.min}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box
              data-test-id="cartPanelFooter"
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                left: 0,
                height: '70px',
                width: '100%',
                zIndex: 200,
                borderTop: '1px solid #DBDBDB',
                boxShadow: '0px -6px 12px 1px rgba(0, 0, 0, 0.08)',
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                px={3}
                py={2}
              >
                <IncDecBtnGroup
                  min={1}
                  value={quantity}
                  onChange={onQuantityChange}
                  data-test-id="inc-dec-button"
                />
                <Button
                  data-test-id="addToCartButton"
                  variant="contained"
                  disabled={!isValid}
                  onClick={onSubmit}
                >
                  {`Add ${quantity} for $${totalPrice}`}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}

export default AddToCartPanel;
